<script setup lang="ts">
import { DiscountCodeProps } from "./DiscountCode.props"
import { NotificationAlertProps } from "~/components/NotificationAlert.props"
import LabelWithIconAndLink from "~/components/Utils/LabelWithIconAndLink.vue"

const props = withDefaults(defineProps<DiscountCodeProps>(), {
  status: "default"
})

defineEmits<{
  (e: "onClickCTA"): void
}>()

const { copy } = useClipboard()
const { addNotification } = useNotification()
const { t } = useI18n()

// So we dont expose the code
const visibleDiscountCode = computed(() =>
  props.status === "default" && props.discountCode
    ? `${props.discountCode.slice(-2)}`
    : `${props.discountCode}`
)

const handleCopyToClipboard = () => {
  copy(props.discountCode)
  const notification: NotificationAlertProps = {
    id: Math.random(),
    type: "success",
    notificationAlertText: t("discounts.copyCodeToClipboard"),
    isWithCloseIcon: true
  }

  addNotification(notification)
}

const stringWithoutEuroSymbol = computed(() =>
  props.discount?.toString().replace("€", "")
)

const splittedPrice = computed(() => {
  if (props.symbol === "€") {
    return stringWithoutEuroSymbol.value.split(",")
  } else {
    return ""
  }
})

const integersPart = computed(() => splittedPrice.value.at(0))
const decimalsPart = computed(() => splittedPrice.value.at(1))

const isDiscountCodeCovered = computed(
  () => props.status === "default" || props.status === "used"
)
</script>

<template>
  <div
    class="
      bg-white
      flex
      h-full
      w-full
      transform-gpu
      flex-col
      gap-4
      rounded-lg
      p-4
      drop-shadow-01
      transition
      hover:drop-shadow-02
    "
  >
    <div class="info flex flex-1 items-start gap-4">
      <div
        class="
          relative
          aspect-square
          h-[136px]
          w-[136px]
          overflow-hidden
          rounded-lg
        "
      >
        <span
          class="
            absolute
            left-1/2
            top-1/2
            z-10
            flex
            -translate-x-1/2 -translate-y-1/2
            items-baseline
            font-Poppins font-medium
            leading-[44.8px]
          "
          :class="[
            status === 'default' ? 'text-white' : 'text-black-main',
            { 'line-through	': status === 'used' }
          ]"
        >
          <span class="dyno">
            <span v-if="symbol === '%'">
              {{ discount }}
            </span>
            <span v-else class="flex items-baseline">
              {{ integersPart }}
              <span class="mouse-bold">,{{ decimalsPart }} </span>
            </span>
          </span>
          <span
            class="pl-[2px]"
            :class="symbol === '%' ? 'pig-bold' : 'mouse-bold'"
          >
            {{ symbol }}
          </span>
        </span>
        <IconsDiscountRibbon
          class="
            absolute
            left-[24px]
            top-[24px]
            z-0
            h-[88px]
            w-[88px]
            scale-150
          "
          :class="{
            'text-green-main': status === 'default',
            'text-tiffany-60':
              status === 'ready' ||
              status === 'applied' ||
              status === 'applying',
            'text-slate-20': status === 'used'
          }"
        />
      </div>
      <div class="text-info flex flex-1 flex-col gap-1">
        <LabelWithIconAndLink
          :label="$t(`discounts.${status}.info${ctaFunction}`)"
          labelIcon="medicine"
        />
        <h4 class="pig-bold md:dolphin-bold text-green-main">
          {{ $t(`discounts.default.extra`, { n: discount }) }}
        </h4>
        <div v-if="minOrder" class="flex items-start gap-2">
          <strong class="mouse-bold"
            >{{ $t("discounts.minOrderLabel") }}:</strong
          >
          <span class="mouse">{{ minOrder }}€</span>
        </div>
        <p class="mouse flex items-start">
          {{ $t("discounts.minOrderText") }}
        </p>
        <div class="mouse-medium text-green-main flex items-center">
          <UtilsMarkdown
            :content="$t(`discounts.default.instructions${ctaFunction}`)"
          />
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
      <div class="relative w-full">
        <div class="relative overflow-hidden rounded-lg">
          <UtilsButton
            :text="`${visibleDiscountCode}`"
            fluid
            theme="outlined-green"
            :class="[
              'custom-btn-discount-code-brand',
              isDiscountCodeCovered ? 'locked' : 'unlocked'
            ]"
            :classText="
              visibleDiscountCode.length < 4 ? 'w-full text-right' : ''
            "
            order="last"
          />
          <div
            v-if="isDiscountCodeCovered"
            class="absolute inset-0 right-10 flex items-center justify-center"
            :class="[
              status === 'default'
                ? 'bg-tiffany-400 text-white'
                : 'bg-slate-20 text-slate-80'
            ]"
          >
            {{ $t("discounts.discountCode") }}
          </div>
        </div>
        <IconsFoldedLip
          v-if="isDiscountCodeCovered"
          class="absolute right-[25px] top-0"
          :class="[status === 'default' ? 'text-green-70' : 'text-slate-40']"
        />
      </div>
      <!-- CTA 2 -->
      <UtilsButton
        :key="visibleDiscountCode + status"
        :class="{
          '[&>span]:!bg-positive-main [&>span]:!text-white':
            status === 'applied'
        }"
        :text="
          status === 'default'
            ? $t(`discounts.${status}.ctaText${ctaFunction}`)
            : $t(`discounts.${status}.ctaText`)
        "
        order="last"
        :is-disabled="status === 'used' || status === 'applied'"
        @click="$emit('onClickCTA')"
      >
        <template #icon>
          <UtilsLoader v-if="status === 'applying'" />
          <IconsCheckmark
            v-if="status === 'used' || status === 'applied'"
            class="h-5 w-5"
          />
        </template>
      </UtilsButton>
    </div>

    <UtilsMarkdown
      v-if="$te(`discounts.${status}.label${ctaFunction}`)"
      class="mouse markdown__link-underlined mb-4"
      :content="$t(`discounts.${status}.label${ctaFunction}`)"
    />
  </div>
</template>
